import type { Address } from "viem"
import { useQuery } from "@tanstack/react-query"

import type { WeiValue } from "@/domains/core"
import type { MulticallContractConfig } from "@/domains/on-chain"
import { useScaleApplication } from "@/domains/application/use-scale-application"

const minimalTokenAbi = [
  {
    constant: true,
    inputs: [{ name: "_owner", type: "address" }],
    name: "balanceOf",
    outputs: [{ name: "balance", type: "uint256" }],
    payable: false,
    stateMutability: "view",
    type: "function",
  },
  {
    constant: true,
    inputs: [
      { name: "_owner", type: "address" },
      { name: "_spender", type: "address" },
    ],
    name: "allowance",
    outputs: [{ name: "remaining", type: "uint256" }],
    payable: false,
    stateMutability: "view",
    type: "function",
  },
] as const

type CreateNfpPositionLensResult = {
  token0: {
    balance: WeiValue
    allowance: WeiValue
  }
  token1: {
    balance: WeiValue
    allowance: WeiValue
  }
}

//#region useCreateNfpPositionLens
export const useCreateNfpPositionLens = ({
  account,
  chainId,
  token0Address,
  token1Address,
  nfpManagerAddress,
  token0Decimals,
  token1Decimals,
}: {
  account: Address | undefined
  chainId: number
  nfpManagerAddress: Address
  token0Address: Address | undefined
  token1Address: Address | undefined
  token0Decimals: number
  token1Decimals: number
}) => {
  const contracts: MulticallContractConfig[] = [
    {
      address: token0Address as unknown as string,
      abi: minimalTokenAbi,
      functionName: "balanceOf",
      args: [account],
      chainId,
    },
    {
      address: token1Address as unknown as string,
      abi: minimalTokenAbi,
      functionName: "balanceOf",
      args: [account],
      chainId,
    },
    // check router allowances for both tokens
    {
      address: token0Address as unknown as string,
      abi: minimalTokenAbi,
      functionName: "allowance",
      args: [account, nfpManagerAddress],
      chainId,
    },
    {
      address: token1Address as unknown as string,
      abi: minimalTokenAbi,
      functionName: "allowance",
      args: [account, nfpManagerAddress],
      chainId,
    },
  ] as const

  const scaleApp = useScaleApplication()

  // @todo MIGRATE: useQuery to MobX store with OrchestratedQuery
  const createNfpPositionLensQuery = useQuery({
    queryKey: ["create-nfp-position-lens", chainId, account, token0Address, token1Address],
    queryFn: async () => {
      if (!token0Address) {
        return undefined
      }
      if (!token1Address) {
        return undefined
      }

      const publicClient = scaleApp.getPublicClient()

      // @ts-expect-error multicall is messy, ignore this error
      const lens = await publicClient.multicall({ contracts })

      const result: CreateNfpPositionLensResult = {
        token0: {
          balance: {
            wei: (lens[0]?.result as bigint | undefined) ?? 0n,
            decimals: token0Decimals,
          },
          allowance: {
            wei: (lens[2]?.result as bigint | undefined) ?? 0n,
            decimals: token0Decimals,
          },
        },
        token1: {
          balance: {
            wei: (lens[1]?.result as bigint | undefined) ?? 0n,
            decimals: token1Decimals,
          },
          allowance: {
            wei: (lens[3]?.result as bigint | undefined) ?? 0n,
            decimals: token1Decimals,
          },
        },
      }
      return result
    },
    enabled: Boolean(account && token0Address && token1Address),
  })

  let createNfpPositionLens = {
    token0: {
      balance: {
        wei: 0n,
        decimals: token0Decimals,
      },
      allowance: {
        wei: 0n,
        decimals: token0Decimals,
      },
    },
    token1: {
      balance: {
        wei: 0n,
        decimals: token1Decimals,
      },
      allowance: {
        wei: 0n,
        decimals: token1Decimals,
      },
    },
  }

  if (createNfpPositionLensQuery?.data) {
    createNfpPositionLens = createNfpPositionLensQuery.data
  }

  return { createNfpPositionLens, createNfpPositionLensQuery }
}
//#endregion
